import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : 'https://api.daggpt.network';

function getAuthHeaders(userId) {
  const cookieToken = document.cookie.match(/daggpt_token=([^;]+)/)?.[1];
  const lsToken = localStorage.getItem('daggpt_token') || localStorage.getItem('token');
  const token = cookieToken || lsToken || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(userId ? { 'x-user-id': userId } : {}),
  };
}

// ─── Design tokens ──────────────────────────────────────────────
const T = {
  bg:          '#ffffff',
  shadow:      '8px 8px 18px #cccce6, -8px -8px 18px #ffffff',
  shadowSm:    '5px 5px 12px #cccce6, -5px -5px 12px #ffffff',
  shadowInset: 'inset 4px 4px 10px #cccce6, inset -4px -4px 10px #ffffff',
  purple:      '#7c3aed',
  purpleMid:   '#6d28d9',
  purpleLight: '#ede9fe',
  purpleFaint: '#f5f3ff',
  textPrimary: '#1e1b4b',
  textSecond:  '#5b5e8b',
  textMuted:   '#9ca3b8',
  border:      'rgba(124,58,237,0.12)',
};

// Smart DGCC formatter - auto-selects precision so small amounts never round to 0
function fmtDgcc(n) {
  const v = parseFloat(n ?? 0);
  if (v === 0) return '0';
  if (Math.abs(v) >= 100)    return v.toFixed(2);
  if (Math.abs(v) >= 1)      return v.toFixed(4);
  if (Math.abs(v) >= 0.0001) return v.toFixed(6);
  return v.toFixed(8);
}
const fmt     = (n, dp = 4) => (+(n ?? 0)).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: dp });
const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
// Strip backend audit trail ("| base=... markup=..." suffix) - users only see the clean label
const fmtDesc = (desc, type) => {
  if (!desc) return type || 'Transaction';
  // Remove the audit suffix starting at "| base="
  const clean = desc.split('| base=')[0].trim().replace(/\|\s*$/, '').trim();
  return clean || type || 'Transaction';
};

// ─── SVG icon set (no emojis) ───────────────────────────────────
const Icon = ({ name, size = 16, color = T.purple }) => {
  const paths = {
    wallet:   'M3 7a2 2 0 012-2h14a2 2 0 012 2v1H3V7zm0 3h18v7a2 2 0 01-2 2H5a2 2 0 01-2-2v-7zm13 2a1 1 0 100 2h2a1 1 0 100-2h-2z',
    cart:     'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-1.4 5M17 13l1.4 5M9 21a1 1 0 100-2 1 1 0 000 2zm8 0a1 1 0 100-2 1 1 0 000 2z',
    zap:      'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
    star:     'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
    transfer: 'M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4',
    receipt:  'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
    search:   'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    chevronD: 'M19 9l-7 7-7-7',
    chevronU: 'M5 15l7-7 7 7',
    undo:     'M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6',
    coin:     'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    lock:     'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
    filter:   'M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z',
  };
  const isDouble = name === 'cart' || name === 'transfer' || name === 'lock';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      {(paths[name] || '').split('M').filter(Boolean).map((seg, i) => (
        <path key={i} d={`M${seg}`} />
      ))}
    </svg>
  );
};

// ─── Shared neumorphic card ─────────────────────────────────────
function Card({ children, style = {}, inset = false }) {
  return (
    <div style={{
      background: T.bg,
      borderRadius: 20,
      boxShadow: inset ? T.shadowInset : T.shadowSm,
      ...style,
    }}>
      {children}
    </div>
  );
}

// ─── Stat card ─────────────────────────────────────────────────
function StatCard({ label, value, sub, iconName, accent = false }) {
  return (
    <Card style={{ padding: '22px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: accent ? `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})` : T.bg,
          boxShadow: accent ? `0 6px 16px rgba(124,58,237,0.35)` : T.shadowSm,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name={iconName} size={17} color={accent ? '#fff' : T.purple} />
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
          textTransform: 'uppercase', color: T.textMuted,
        }}>{label}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: T.textPrimary, letterSpacing: -0.5, marginBottom: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 500 }}>{sub}</div>}
    </Card>
  );
}

// ─── Type config (NO emojis - SVG icons only) ───────────────────
const TYPE_CFG = {
  purchase:      { label: 'Purchase',  icon: 'cart',     shade: T.purple },
  bonus:         { label: 'Bonus',     icon: 'star',     shade: '#7c3aed' },
  deduction:     { label: 'Usage',     icon: 'zap',      shade: '#5b21b6' },
  refund:        { label: 'Refund',    icon: 'undo',     shade: '#8b5cf6' },
  adjustment:    { label: 'Adjust',   icon: 'settings',  shade: '#a78bfa' },
  transfer:      { label: 'Transfer', icon: 'transfer',  shade: '#6d28d9' },
  dgcc_transfer: { label: 'Transfer', icon: 'transfer',  shade: '#6d28d9' },
};

const FILTER_TABS = [
  { key: 'all',           label: 'All' },
  { key: 'purchase',      label: 'Purchases' },
  { key: 'dgcc_transfer', label: 'Transfers' },
  { key: 'bonus',         label: 'Bonuses' },
  { key: 'deduction',     label: 'Usage' },
  { key: 'refund',        label: 'Refunds' },
];

const PAGE_SIZE = 20;

// ═══════════════════════════════════════════════════════════════
// DGCC COINS SECTION
// ═══════════════════════════════════════════════════════════════
export function CreditsSection({ userId }) {
  const [data,       setData]       = useState(null);
  const [txns,       setTxns]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [txLoading,  setTxLoading]  = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [search,     setSearch]     = useState('');
  const [sortField,  setSortField]  = useState('created_at');
  const [sortDir,    setSortDir]    = useState('desc');
  const [page,       setPage]       = useState(1);
  const [hasMore,    setHasMore]    = useState(false);

  const headers = getAuthHeaders(userId);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_URL}/api/billing/balance`, { headers })
      .then(r => r.json()).then(setData).catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const fetchTxns = useCallback(async (pg = 1, type = 'all', reset = true) => {
    if (!userId) return;
    setTxLoading(true);
    try {
      const tp = type !== 'all' ? `&type=${type}` : '';
      const res = await fetch(`${API_URL}/api/billing/transactions?page=${pg}&limit=${PAGE_SIZE}${tp}`, { headers });
      const d = await res.json();
      const rows = d.transactions || [];
      setTxns(prev => reset ? rows : [...prev, ...rows]);
      setHasMore(rows.length === PAGE_SIZE);
    } catch {}
    finally { setTxLoading(false); }
  }, [userId]);

  useEffect(() => { setPage(1); fetchTxns(1, filterType, true); }, [filterType, userId]);

  const visible = txns
    .filter(tx => !search || (tx.description || '').toLowerCase().includes(search.toLowerCase()) || (tx.type || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const m = sortDir === 'asc' ? 1 : -1;
      if (sortField === 'amount') return m * (a.amount - b.amount);
      if (sortField === 'balance_after') return m * (a.balance_after - b.balance_after);
      return m * (new Date(a.created_at) - new Date(b.created_at));
    });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const pct = data
    ? Math.min(100, Math.round((data.balance / Math.max((data.total_purchased || 0) + (data.total_bonus || 0), 1)) * 100))
    : 0;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px 0', gap: 12, color: T.textMuted }}>
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
        style={{ width: 20, height: 20, border: `2px solid ${T.purpleLight}`, borderTopColor: T.purple, borderRadius: '50%' }} />
      <span style={{ fontSize: 14, fontWeight: 500 }}>Loading wallet...</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <style>{`
        @media (max-width: 1024px) {
          .credits-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 640px) {
          .credits-stats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Stat Cards ── */}
      <div className="credits-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 18 }}>
        <StatCard iconName="coin"   label="DGCC Balance"    value={`${fmtDgcc(data?.balance)} DGCC`}           sub={`≈ $${(+(data?.balance ?? 0)).toFixed(4)} USD`} accent />
        <StatCard iconName="cart"   label="Total Purchased" value={`${fmtDgcc(data?.total_purchased)} DGCC`}   sub={`≈ $${(+(data?.total_purchased ?? 0)).toFixed(4)} USD`} />
        <StatCard iconName="zap"    label="Total Spent"     value={`${fmtDgcc(data?.total_spent)} DGCC`}       sub={`≈ $${(+(data?.total_spent ?? 0)).toFixed(4)} USD`} />
        <StatCard iconName="star"   label="Bonus Received"  value={`${fmtDgcc(data?.total_bonus)} DGCC`}       sub="Lifetime grants" />
      </div>

      {/* ── Balance Bar ── */}
      <Card style={{ padding: '24px 28px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, letterSpacing: 0.2 }}>DGCC Coin Balance</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>1 DGCC = $1.00 USD · Credits never expire</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="lock" size={13} color={T.textMuted} />
            <span style={{ fontSize: 12, fontWeight: 700, color: T.purple }}>{fmtDgcc(data?.balance)} DGCC remaining</span>
          </div>
        </div>
        {/* Track */}
        <div style={{ height: 8, background: T.bg, borderRadius: 100, overflow: 'hidden', boxShadow: T.shadowInset }}>
          <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1.4, ease: [0.4, 0, 0.2, 1] }}
            style={{ height: '100%', borderRadius: 100, background: `linear-gradient(90deg, ${T.purple}, ${T.purpleMid})`, boxShadow: `0 0 12px rgba(124,58,237,0.4)` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <span style={{ fontSize: 11, color: T.textMuted }}>0 DGCC</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>{fmtDgcc((data?.total_purchased || 0) + (data?.total_bonus || 0))} DGCC (lifetime earned)</span>
        </div>
      </Card>

      {/* ── Transaction Table ── */}
      <Card style={{ overflow: 'hidden' }}>
        {/* Table header controls */}
        <div style={{ padding: '20px 24px 0', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, letterSpacing: -0.2 }}>Transaction History</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3 }}>{txns.length} records loaded</div>
            </div>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
                <Icon name="search" size={14} color={T.textMuted} />
              </div>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search transactions..."
                style={{
                  paddingLeft: 36, paddingRight: 14, paddingTop: 9, paddingBottom: 9,
                  borderRadius: 12, border: `1px solid ${T.border}`,
                  background: T.bg, boxShadow: T.shadowInset, outline: 'none',
                  fontSize: 12, color: T.textPrimary, width: 210, fontFamily: 'inherit',
                }} />
            </div>
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: 6, paddingBottom: 0, overflowX: 'auto' }}>
            {FILTER_TABS.map(tab => {
              const active = filterType === tab.key;
              return (
                <button key={tab.key} onClick={() => setFilterType(tab.key)}
                  style={{
                    padding: '7px 16px', borderRadius: 100, border: 'none',
                    background: active ? `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})` : T.bg,
                    boxShadow: active
                      ? `0 4px 14px rgba(124,58,237,0.3)`
                      : `inset 2px 2px 5px #d0d0e8, inset -2px -2px 5px rgba(255,255,255,0.65)`,
                    color: active ? '#fff' : T.textSecond,
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
                    cursor: 'pointer', transition: 'all 0.18s', whiteSpace: 'nowrap',
                    marginBottom: 16,
                  }}>
                  {tab.label}
                </button>

              );
            })}
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead>
              <tr style={{ background: T.purpleFaint }}>
                {[
                  { label: 'Type',          field: null,           w: 120 },
                  { label: 'Description',   field: null,           w: null },
                  { label: 'Date',          field: 'created_at',   w: 170 },
                  { label: 'Amount',        field: 'amount',       w: 120 },
                  { label: 'Balance After', field: 'balance_after',w: 140 },
                  { label: 'Reference',     field: null,           w: 130 },
                ].map(col => (
                  <th key={col.label}
                    onClick={col.field ? () => toggleSort(col.field) : undefined}
                    style={{
                      padding: '11px 18px', textAlign: 'left',
                      fontSize: 10, fontWeight: 800, letterSpacing: 1,
                      color: T.textMuted, textTransform: 'uppercase',
                      borderBottom: `1px solid ${T.border}`,
                      cursor: col.field ? 'pointer' : 'default',
                      userSelect: 'none', whiteSpace: 'nowrap',
                      width: col.w || undefined,
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      {col.label}
                      {col.field && (
                        <span style={{ opacity: sortField === col.field ? 1 : 0.25, fontSize: 9 }}>
                          {sortField === col.field && sortDir === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && !txLoading && (
                <tr>
                  <td colSpan={6}>
                    <div style={{ padding: '60px 24px', textAlign: 'center' }}>
                      <div style={{
                        width: 52, height: 52, borderRadius: 16, margin: '0 auto 14px',
                        background: T.bg, boxShadow: T.shadowSm,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon name="receipt" size={22} color={T.textMuted} />
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary, marginBottom: 6 }}>No transactions found</div>
                      <div style={{ fontSize: 12, color: T.textMuted }}>{search ? `No results for "${search}"` : 'Your transaction history will appear here'}</div>
                    </div>
                  </td>
                </tr>
              )}
              <AnimatePresence>
                {visible.map((tx, i) => {
                  const cfg = TYPE_CFG[tx.type] || TYPE_CFG.adjustment;
                  const isCredit = tx.amount > 0;
                  return (
                    <motion.tr key={tx.id || i}
                      initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.015, 0.25) }}
                      style={{ borderBottom: `1px solid ${T.border}`, transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = T.purpleFaint}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                      {/* Type */}
                      <td style={{ padding: '13px 18px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                            background: T.bg, boxShadow: T.shadowSm,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Icon name={cfg.icon} size={13} color={cfg.shade} />
                          </div>
                          <span style={{
                            fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
                            color: cfg.shade,
                            padding: '3px 8px', borderRadius: 6,
                            background: T.purpleLight,
                          }}>{cfg.label}</span>
                        </div>
                      </td>

                      {/* Description */}
                      <td style={{ padding: '13px 18px' }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: T.textPrimary, lineHeight: 1.5 }}>
                          {fmtDesc(tx.description, tx.type)}
                        </div>
                      </td>

                      {/* Date */}
                      <td style={{ padding: '13px 18px', fontSize: 12, color: T.textSecond, whiteSpace: 'nowrap' }}>
                        {fmtDate(tx.created_at)}
                      </td>

                      {/* Amount */}
                      <td style={{ padding: '13px 18px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          fontSize: 13, fontWeight: 800, letterSpacing: -0.3,
                          color: isCredit ? T.purple : T.textSecond,
                        }}>
                          {isCredit ? '+' : ''}{fmt(tx.amount, 4)} DGCC
                        </span>
                      </td>

                      {/* Balance after */}
                      <td style={{ padding: '13px 18px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: T.textPrimary }}>
                          {fmt(tx.balance_after, 4)} DGCC
                        </span>
                      </td>

                      {/* Reference */}
                      <td style={{ padding: '13px 18px' }}>
                        {tx.task_id || tx.model_id ? (
                          <span style={{
                            fontSize: 10, fontWeight: 600, letterSpacing: 0.3,
                            color: T.textMuted, background: T.bg, boxShadow: T.shadowInset,
                            padding: '3px 9px', borderRadius: 6, fontFamily: 'monospace',
                          }}>
                            {(tx.model_id || tx.task_id?.slice(0, 10) + '…')}
                          </span>
                        ) : <span style={{ color: T.textMuted, fontSize: 12 }}>-</span>}
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>

          {txLoading && (
            <div style={{ padding: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}
                style={{ width: 16, height: 16, border: `2px solid ${T.purpleLight}`, borderTopColor: T.purple, borderRadius: '50%' }} />
              <span style={{ fontSize: 12, color: T.textMuted }}>Loading...</span>
            </div>
          )}

          {hasMore && !txLoading && (
            <div style={{ padding: '16px 24px', textAlign: 'center', borderTop: `1px solid ${T.border}` }}>
              <button onClick={() => { const next = page + 1; setPage(next); fetchTxns(next, filterType, false); }}
                style={{
                  padding: '9px 28px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: T.bg, boxShadow: T.shadowSm, color: T.purple,
                  fontSize: 12, fontWeight: 700, letterSpacing: 0.3, transition: 'all 0.15s',
                }}>
                Load more transactions
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// BUY DGCC SECTION
// ═══════════════════════════════════════════════════════════════
const DGCC_PACKS = [
  { id: 'starter', label: 'Starter',  amount: 5,   badge: null },
  { id: 'basic',   label: 'Basic',    amount: 10,  badge: null },
  { id: 'popular', label: 'Popular',  amount: 25,  badge: 'Most Popular' },
  { id: 'pro',     label: 'Pro',      amount: 50,  badge: null },
  { id: 'studio',  label: 'Studio',   amount: 100, badge: 'Best Value' },
];

export function PlansSection({ userId }) {
  const [selectedId,   setSelectedId]   = useState('popular');
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom,     setIsCustom]     = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');

  const selected = DGCC_PACKS.find(p => p.id === selectedId);
  const amount   = isCustom ? (parseInt(customAmount) || 0) : (selected?.amount || 0);
  const valid    = amount >= 5;

  const handleCheckout = async () => {
    if (!userId) return setError('Please sign in to continue.');
    if (!valid)  return setError('Minimum purchase is 5 DGCC ($5.00).');
    setLoading(true); setError('');
    try {
      const res  = await fetch(`${API_URL}/api/billing/create-custom-checkout`, {
        method: 'POST', headers: getAuthHeaders(userId), body: JSON.stringify({ amountUsd: amount }),
      });
      const data = await res.json();
      if (data.checkoutUrl) window.location.href = data.checkoutUrl;
      else setError(data.error || 'Unable to create checkout session.');
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} style={{ maxWidth: 660 }}>

      {/* Heading */}
      <div style={{ marginBottom: 28, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14, flexShrink: 0,
          background: `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})`,
          boxShadow: `0 6px 18px rgba(124,58,237,0.35)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name="coin" size={20} color="#fff" />
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: T.textPrimary, letterSpacing: -0.4 }}>Get DGCC Coins</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>$1.00 USD = 1 DGCC Coin · Credits never expire</div>
        </div>
      </div>

      {/* Packages */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
        {DGCC_PACKS.map(pack => {
          const active = !isCustom && selectedId === pack.id;
          return (
            <motion.div key={pack.id} whileTap={{ scale: 0.998 }}
              onClick={() => { setSelectedId(pack.id); setIsCustom(false); setCustomAmount(''); setError(''); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 18px', borderRadius: 14, cursor: 'pointer',
                background: active ? T.purpleLight : '#fff',
                border: `1.5px solid ${active ? T.purple : '#e5e7eb'}`,
                boxShadow: active ? `0 0 0 3px rgba(124,58,237,0.10)` : 'none',
                transition: 'all 0.14s',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                {/* Radio */}
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                  border: active ? `5px solid ${T.purple}` : '1.5px solid #d1d5db',
                  background: active ? '#fff' : 'transparent', transition: 'all 0.14s',
                }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>{pack.label}</div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>{pack.amount} DGCC Coins</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {pack.badge && (
                  <span style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                    color: T.purple, background: T.purpleLight,
                    padding: '3px 9px', borderRadius: 6,
                  }}>{pack.badge}</span>
                )}
                <span style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary }}>${pack.amount}</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Custom input */}
      <div onClick={() => { setIsCustom(true); setError(''); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '13px 18px',
          borderRadius: 14, cursor: 'text', marginBottom: 26,
          background: isCustom ? T.purpleLight : '#fff',
          border: `1.5px solid ${isCustom ? T.purple : '#e5e7eb'}`,
          boxShadow: isCustom ? `0 0 0 3px rgba(124,58,237,0.10)` : 'none', transition: 'all 0.14s',
        }}>
        <div style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, border: isCustom ? `5px solid ${T.purple}` : '1.5px solid #d1d5db', background: isCustom ? '#fff' : 'transparent', transition: 'all 0.14s' }} />
        <span style={{ fontSize: 14, fontWeight: 500, color: T.textPrimary, whiteSpace: 'nowrap' }}>Custom amount</span>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }} onClick={e => e.stopPropagation()}>
          <span style={{ fontSize: 14, color: T.textMuted, marginRight: 2 }}>$</span>
          <input type="number" min="5" value={customAmount} placeholder="Min. $5"
            onFocus={() => { setIsCustom(true); setError(''); }}
            onChange={e => { setCustomAmount(e.target.value); setIsCustom(true); setError(''); }}
            style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, fontWeight: 500, color: T.textPrimary, fontFamily: 'inherit' }}
          />
          {isCustom && amount >= 5 && (
            <span style={{ fontSize: 11, color: T.purple, fontWeight: 700, whiteSpace: 'nowrap' }}>{amount} DGCC</span>
          )}
        </div>
      </div>

      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ background: '#fdf2f8', border: '1px solid #e9d5e9', color: '#7c2d7c', padding: '10px 14px', borderRadius: 10, fontSize: 12, marginBottom: 18, fontWeight: 500 }}>
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <motion.button onClick={handleCheckout} disabled={!valid || loading}
          whileHover={valid && !loading ? { opacity: 0.88 } : {}}
          whileTap={valid && !loading ? { scale: 0.99 } : {}}
          style={{
            padding: '12px 30px', border: 'none', borderRadius: 12,
            background: valid ? `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})` : '#e5e7eb',
            color: valid ? '#fff' : '#9ca3af',
            fontSize: 13, fontWeight: 700, cursor: valid ? 'pointer' : 'not-allowed',
            boxShadow: valid ? `0 6px 20px rgba(124,58,237,0.35)` : 'none',
            transition: 'all 0.15s', letterSpacing: 0.3,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
          {loading ? (
            <><motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.75, ease: 'linear' }}
              style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%' }} />
              Redirecting...</>
          ) : valid ? `Purchase ${amount} DGCC · $${amount}` : 'Select a package'}
        </motion.button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="lock" size={12} color={T.textMuted} />
          <span style={{ fontSize: 11, color: T.textMuted }}>Secured by Stripe</span>
        </div>
      </div>
      {valid && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ margin: '12px 0 0', fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>
          You will be redirected to Stripe's secure checkout for <strong style={{ color: T.textSecond }}>${amount}</strong> → <strong style={{ color: T.purple }}>{amount} DGCC Coin{amount > 1 ? 's' : ''}</strong> credited to your wallet instantly.
        </motion.p>
      )}
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════
// USAGE SECTION
// ═══════════════════════════════════════════════════════════════
export function UsageSection({ userId }) {
  const [txns,    setTxns]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API_URL}/api/billing/transactions?type=deduction&limit=100`, { headers: getAuthHeaders(userId) })
      .then(r => r.json()).then(d => setTxns(d.transactions || [])).finally(() => setLoading(false));
  }, [userId]);

  const byType    = txns.reduce((acc, tx) => {
    const t = tx.generation_type || 'chat';
    if (!acc[t]) acc[t] = { count: 0, dgcc: 0 };
    acc[t].count++; acc[t].dgcc += Math.abs(tx.amount);
    return acc;
  }, {});
  const totalDgcc = Object.values(byType).reduce((s, v) => s + v.dgcc, 0);

  const typeConfig = {
    chat:    { label: 'AI Chat',         icon: 'receipt' },
    image:   { label: 'Image Generation',icon: 'filter' },
    video:   { label: 'Video Generation',icon: 'zap' },
    music:   { label: 'Music Generation',icon: 'star' },
    website: { label: 'Website Builder', icon: 'settings' },
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 0', gap: 10, color: T.textMuted }}>
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
        style={{ width: 18, height: 18, border: `2px solid ${T.purpleLight}`, borderTopColor: T.purple, borderRadius: '50%' }} />
      <span style={{ fontSize: 13 }}>Loading usage data...</span>
    </div>
  );

  if (txns.length === 0) return (
    <div style={{ textAlign: 'center', padding: '80px 0' }}>
      <div style={{ width: 56, height: 56, borderRadius: 18, margin: '0 auto 16px', background: T.bg, boxShadow: T.shadowSm, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name="zap" size={24} color={T.textMuted} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, marginBottom: 6 }}>No usage data yet</div>
      <div style={{ fontSize: 12, color: T.textMuted }}>Start generating to see your DGCC consumption breakdown here</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <style>{`
        @media (max-width: 1024px) {
          .usage-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 640px) {
          .usage-stats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <div className="usage-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 18 }}>
        {Object.entries(byType).map(([type, stats]) => {
          const cfg = typeConfig[type] || { label: type, icon: 'zap' };
          return <StatCard key={type} iconName={cfg.icon} label={cfg.label} value={`${fmt(stats.dgcc)} DGCC`} sub={`${stats.count} generation${stats.count !== 1 ? 's' : ''}`} />;
        })}
      </div>
      <Card style={{ padding: '24px 28px' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, marginBottom: 22, letterSpacing: -0.2 }}>DGCC Consumption by Category</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {Object.entries(byType).map(([type, stats]) => {
            const cfg = typeConfig[type] || { label: type };
            const pct = totalDgcc > 0 ? Math.round((stats.dgcc / totalDgcc) * 100) : 0;
            return (
              <div key={type}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>{cfg.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.purple }}>{fmt(stats.dgcc)} DGCC <span style={{ color: T.textMuted, fontWeight: 500 }}>({pct}%)</span></span>
                </div>
                <div style={{ height: 6, background: T.bg, borderRadius: 100, overflow: 'hidden', boxShadow: T.shadowInset }}>
                  <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1.1, ease: [0.4, 0, 0.2, 1] }}
                    style={{ height: '100%', borderRadius: 100, background: `linear-gradient(90deg, ${T.purple}, ${T.purpleMid})` }} />
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
